import Section from '../shared/Section';
import Field from '../shared/Field';
import Input from '../shared/Input';
import Select from '../shared/Select';
import Grid from '../shared/Grid';
import PayerPicker from '../shared/PayerPicker';

const CHANNEL_OPTIONS = [
  { value: 'office_ally', label: 'Office Ally' },
  { value: 'claim_md', label: 'Claim.MD' },
  { value: 'fax', label: 'Fax' },
  { value: 'export', label: 'Export' },
];

export const CLAIM_FILING_OPTIONS = [
  { value: '', label: 'Select type' },
  { value: 'MC', label: 'Medicare' },
  { value: 'MA', label: 'Medicaid' },
  { value: 'BL', label: 'Blue Cross Blue Shield' },
  { value: 'CI', label: 'Commercial Insurance' },
  { value: 'HM', label: 'HMO' },
  { value: 'OF', label: 'Other Federal' },
  { value: 'WC', label: 'Workers Comp' },
  { value: 'ZZ', label: 'Other' },
];

/**
 * Submission Channel here is a UI hint only — sets the SubmitModal default.
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   setMany: (updates: any) => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 *   channelHint: string,
 *   setChannelHint: (channel: string) => void,
 * }} props
 */
export default function PayerRoutingSection({ claim, setField, setMany, editable, errorsByField, channelHint, setChannelHint }) {
  return (
    <Section icon="send" title="Payer & Routing" subtitle="Destination payer and electronic routing">
      <Grid cols={3}>
        <Field label="Destination Payer" required error={errorsByField?.get('payer_snapshot.payer_id')?.message}>
          <PayerPicker
            value={claim.payer_snapshot?.payer_id}
            payerSnapshot={claim.payer_snapshot}
            onSelect={(/** @type {any} */ p) => {
              // Wire shape only has `payer_snapshot`; there's no separate
              // `payer` object on the claim. Writing to claim.payer is silently
              // dropped by DRF and clobbered on autosave response.
              if (!p) {
                setField('payer_snapshot', null);
                return;
              }
              const updates = /** @type {Record<string, any>} */ ({
                payer_snapshot: {
                  payer_id: p.payer_id,
                  name: p.name,
                  address_line_1: p.address_line_1 || '',
                  city: p.city || '',
                  state: p.state || '',
                  zip_code: p.zip_code || '',
                },
              });
              // Pre-fill claim_filing_indicator with the payer's hint when
              // available, falling back to "CI" (commercial). Validator
              // requires this field; without a default the user sees a
              // dangling "claim_filing_indicator is required" error and there
              // is no UI to set it directly.
              if (!claim.claim_filing_indicator) {
                updates.claim_filing_indicator = p.claim_filing_indicator || 'CI';
              }
              setMany(updates);
            }}
            disabled={!editable}
          />
        </Field>
        <Field label="Payer ID" required caption="EDI clearinghouse ID">
          <Input
            data-field="payer_snapshot.payer_id"
            value={claim.payer_snapshot?.payer_id || ''}
            onChange={(/** @type {any} */ e) => setField('payer_snapshot.payer_id', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Insurance type" required error={errorsByField?.get('claim_filing_indicator')?.message}>
          <Select
            data-field="claim_filing_indicator"
            value={claim.claim_filing_indicator || ''}
            options={CLAIM_FILING_OPTIONS}
            onChange={(/** @type {string} */ v) => setField('claim_filing_indicator', v)}
            disabled={!editable}
          />
        </Field>
        <Field label="Submission channel" caption="Sets default for Submit modal">
          <Select
            value={channelHint || 'office_ally'}
            options={CHANNEL_OPTIONS}
            onChange={setChannelHint}
            disabled={!editable}
          />
        </Field>
      </Grid>
    </Section>
  );
}
